/**
 * Public plan pricing — the live Stripe prices behind our plan lookup keys,
 * with no auth. Marketing pages need the same numbers coaches are charged, and
 * a visitor to the pricing page has no session.
 *
 * Nothing customer-specific is exposed: this is the published price list, the
 * same information printed on the pricing page today. Trials, referral coupons
 * and the coach's own subscription stay behind `~/lib/functions/nova-billing`.
 *
 * Aliases: public pricing, plan prices, pricing page prices
 */

import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";

import type { NovaPlan } from "~/lib/server/nova-billing";

/** Live prices for every configured plan, monthly and yearly. */
export const getPublicPlanPricing = createServerFn({ method: "GET" }).handler(
  async (): Promise<NovaPlan[]> => {
    // Server-side cache, not just the query's staleTime: a fresh QueryClient is
    // built per SSR request, so nothing is shared between visitors and this
    // endpoint is unauthenticated.
    const { fetchPublicPlans } =
      await import("~/lib/server/public-plan-pricing");
    return fetchPublicPlans();
  },
);

export const publicPlanPricingOptions = queryOptions({
  queryKey: ["public", "plan-pricing"],
  queryFn: () => getPublicPlanPricing(),
  // Keeps a single visitor's navigations off the network. Cross-visitor
  // protection is the server cache in `~/lib/server/public-plan-pricing` —
  // this alone would not provide any.
  staleTime: 10 * 60 * 1000,
});
